import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

const MobileDebugger = ({ enabled = true }) => {
  const [debugInfo, setDebugInfo] = useState({
    userAgent: '',
    windowSize: { width: 0, height: 0 },
    errors: [],
    warnings: [],
    consoleErrors: []
  });

  useEffect(() => {
    if (!enabled) return;

    // Capture basic info
    const updateDebugInfo = () => {
      setDebugInfo(prev => ({
        ...prev,
        userAgent: navigator.userAgent,
        windowSize: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      }));
    };

    // Capture console errors
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.error = (...args) => {
      setDebugInfo(prev => ({
        ...prev,
        consoleErrors: [...prev.consoleErrors.slice(-9), args.join(' ')]
      }));
      originalConsoleError(...args);
    };

    console.warn = (...args) => {
      setDebugInfo(prev => ({
        ...prev,
        warnings: [...prev.warnings.slice(-9), args.join(' ')]
      }));
      originalConsoleWarn(...args);
    };

    // Capture unhandled errors
    const handleError = (event) => {
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors.slice(-9), `${event.error?.message || event.message} at ${event.filename}:${event.lineno}`]
      }));
    };

    const handlePromiseRejection = (event) => {
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors.slice(-9), `Promise rejection: ${event.reason}`]
      }));
    };

    updateDebugInfo();
    window.addEventListener('resize', updateDebugInfo);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    return () => {
      window.removeEventListener('resize', updateDebugInfo);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, [enabled]);

  if (!enabled) return null;

  const isMobile = debugInfo.windowSize.width <= 768;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 10,
        right: 10,
        zIndex: 9999,
        maxWidth: isMobile ? '90vw' : 400,
      }}
    >
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="caption" color="error">
            🐛 Debug Panel
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 1, maxHeight: 300, overflow: 'auto', fontSize: '0.75rem' }}>
            <Typography variant="caption" display="block" gutterBottom>
              <strong>Screen:</strong> {debugInfo.windowSize.width}×{debugInfo.windowSize.height}
            </Typography>
            
            <Typography variant="caption" display="block" gutterBottom>
              <strong>Mobile:</strong> {isMobile ? 'Yes' : 'No'}
            </Typography>
            
            <Typography variant="caption" display="block" gutterBottom>
              <strong>User Agent:</strong> {debugInfo.userAgent.substring(0, 50)}...
            </Typography>

            {debugInfo.errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 1, fontSize: '0.7rem' }}>
                <Typography variant="caption" display="block">
                  <strong>Errors ({debugInfo.errors.length}):</strong>
                </Typography>
                {debugInfo.errors.map((error, idx) => (
                  <Typography key={idx} variant="caption" display="block">
                    {error}
                  </Typography>
                ))}
              </Alert>
            )}

            {debugInfo.consoleErrors.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1, fontSize: '0.7rem' }}>
                <Typography variant="caption" display="block">
                  <strong>Console Errors ({debugInfo.consoleErrors.length}):</strong>
                </Typography>
                {debugInfo.consoleErrors.map((error, idx) => (
                  <Typography key={idx} variant="caption" display="block">
                    {error}
                  </Typography>
                ))}
              </Alert>
            )}

            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              <strong>React:</strong> {React.version}
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default MobileDebugger;